import { useMemo } from 'react';
import { DatePicker, DatesProvider } from '@mantine/dates';
import dayjs from 'dayjs';
import {
  listValidPickupDates,
  weekdayNameFromIso,
  formatPickupDate,
} from '../utils/pickupDates';
import './PickupScheduler.css';

// Accepts both native Date and dayjs objects (Mantine v8 passes dayjs to callbacks)
const isoOf = (date) => dayjs(date).format('YYYY-MM-DD');

const shortTime = (timeStr) => (typeof timeStr === 'string' ? timeStr.slice(0, 5) : timeStr);

/**
 * Calendar (only bookable days selectable) + time-slot cards for a chosen date.
 * Shared by customer checkout and admin take-order flows.
 */
export default function PickupScheduler({
  windows = [],
  holidays = [],
  pickupDate,
  setPickupDate,
  pickupTime,
  setPickupTime,
  t,
  langCode,
}) {
  // Every bookable date: upcoming occurrences of any window weekday, minus holiday closures
  const validDates = useMemo(
    () => listValidPickupDates({
      windowDays: windows.map((w) => w.day_of_week),
      holidays,
    }),
    [windows, holidays]
  );
  const validSet = useMemo(() => new Set(validDates), [validDates]);

  // Slots offered for the chosen date (its weekday determines the windows)
  const slotsForDate = useMemo(() => {
    if (!pickupDate) return [];
    const day = weekdayNameFromIso(pickupDate);
    return windows
      .filter((w) => w.day_of_week === day)
      .slice()
      .sort((a, b) => String(a.start_time).localeCompare(String(b.start_time)));
  }, [windows, pickupDate]);

  const selectedSlot = pickupTime
    ? slotsForDate.find((w) => `${w.day_of_week} ${w.start_time}-${w.end_time}` === pickupTime)
    : null;

  if (validDates.length === 0) {
    return (
      <p className="pickup-hint" role="status">
        {t('noPickupWindowsConfigured', 'No bookable pickup windows at this location.')}
      </p>
    );
  }

  return (
    <div className="pickup-scheduler">
      <DatesProvider settings={{ locale: langCode === 'de' ? 'de' : 'en', firstDayOfWeek: 1 }}>
        <DatePicker
          value={pickupDate || null}
          onChange={(date) => {
            if (!date) return;
            const iso = isoOf(date);
            setPickupDate(iso);
            setPickupTime('');
          }}
          minDate={new Date(new Date().setHours(0, 0, 0, 0))}
          getDayProps={(date) => ({ disabled: !validSet.has(isoOf(date)) })}
          firstDayOfWeek={1}
          aria-label={t('choosePickupDate', 'Pick a date')}
        />
      </DatesProvider>

      <div className="pickup-slots" aria-live="polite">
        <h5>{t('availableTimeSlots', 'Available time slots')}</h5>
        {!pickupDate ? (
          <p className="pickup-hint">{t('pickDateFirstHint', 'Select a date to see available time slots.')}</p>
        ) : (
          <div className="pickup-slot-group" role="radiogroup" aria-label={t('availableTimeSlots', 'Available time slots')}>
            {slotsForDate.map((w, idx) => {
              const value = `${w.day_of_week} ${w.start_time}-${w.end_time}`;
              return (
                <label key={idx} className={`pickup-slot-card ${pickupTime === value ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name={`pickup-slot-${idx}`}
                    className="sr-only"
                    value={value}
                    checked={pickupTime === value}
                    onChange={() => setPickupTime(value)}
                  />
                  <span className="pickup-slot-time">{shortTime(w.start_time)}–{shortTime(w.end_time)}</span>
                </label>
              );
            })}
          </div>
        )}

        {pickupDate && selectedSlot && (
          <p className="pickup-summary">
            <strong>{t('pickupSlot', 'Pickup')}:</strong>{' '}
            {formatPickupDate(pickupDate, langCode)} ·{' '}
            {shortTime(selectedSlot.start_time)}–{shortTime(selectedSlot.end_time)}
          </p>
        )}
      </div>
    </div>
  );
}
